import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Archive, Circle, Pause, Play, RefreshCw, Table2 } from 'lucide-react'
import clsx from 'clsx'
import { dbConsoleService, type LedgerEntry } from '@/services/dbConsole'
import { count } from './format'
import { dealFileBlocker, openDealFile } from './lifecycle'

const POLL_MS = 4000
const MAX_KEPT = 400

function stamp(iso: string | null): string {
  if (!iso) return '—'
  const parsed = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function cell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/** A soft-deleted row: the ledger lists every write, archived rows included,
 *  and marks them rather than leaving them to read as live. */
function entryArchived(entry: LedgerEntry): boolean {
  const stamp = entry.row.archived_at
  return stamp !== null && stamp !== undefined && stamp !== ''
}

/** The deal id a ledger entry opens as a deal file, or null. */
function entryDealId(entry: LedgerEntry, viewerEnvironment: string | undefined): string | null {
  if (entry.table !== 'loans' || !entry.recordId) return null
  return dealFileBlocker(entry.row, entryArchived(entry), viewerEnvironment) ? null : entry.recordId
}

/**
 * The live write ledger.
 *
 * Perform an action anywhere in the platform, come here, and the row the
 * database actually stored is listed with the timestamp the database assigned
 * it. Rows that arrived since this panel was opened are marked, which is what
 * makes a walkthrough demonstrable rather than assertable.
 *
 * The server rewinds each poll's cursor slightly to guarantee it never misses a
 * write (see `read_ledger`), so entries can repeat across polls — they are
 * deduped here on the server-supplied `key`.
 */
export function LiveLedger() {
  const [live, setLive] = useState(true)
  const [tableFilter, setTableFilter] = useState<string>('')
  const [entries, setEntries] = useState<LedgerEntry[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)

  // Everything at or after this instant is "new since you opened the panel".
  const openedAt = useRef(new Date().toISOString())

  // Feed state travels together with the filter it belongs to. This used to be
  // three refs reset by a `useEffect` keyed on tableFilter — which also fires
  // on mount, so it raced the first fetch: the query resolved and set the rows,
  // then the mount effect cleared them, and since the query never re-ran the
  // panel sat empty reporting "0 rows loaded" against a full database.
  //
  // Resetting inside queryFn removes the race outright: the filter is part of
  // the query key, so queryFn is *already* the thing that runs on a change.
  const feed = useRef<{ filter: string; cursor: string | null; seen: Set<string> }>({
    filter: tableFilter,
    cursor: null,
    seen: new Set(),
  })

  const query = useQuery({
    queryKey: ['db-console', 'ledger', tableFilter],
    queryFn: async () => {
      // Scope changed — start clean rather than mixing two different watches.
      if (feed.current.filter !== tableFilter) {
        feed.current = { filter: tableFilter, cursor: null, seen: new Set() }
        setEntries([])
      }
      const payload = await dbConsoleService.ledger({
        since: feed.current.cursor,
        limit: 200,
        tables: tableFilter || null,
      })
      const fresh = payload.entries.filter((entry) => !feed.current.seen.has(entry.key))
      fresh.forEach((entry) => feed.current.seen.add(entry.key))
      if (fresh.length) {
        setEntries((prev) => [...fresh, ...prev].slice(0, MAX_KEPT))
      }
      if (payload.newestAt) feed.current.cursor = payload.newestAt
      else if (!feed.current.cursor) feed.current.cursor = payload.queriedAt
      return payload
    },
    refetchInterval: live ? POLL_MS : false,
    // React Query suppresses interval refetching while the window is
    // unfocused. That is a sensible default for ordinary data, and the wrong
    // one here: this panel claims to be live and shows a pulsing indicator, so
    // a feed that quietly stalls whenever the window loses focus — a demo run
    // side-by-side with something else, a second monitor — would be telling
    // the viewer something untrue. Polling is bounded anyway: the panel
    // unmounts when another tab is selected, so it only runs while on screen.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
  })

  const sources = query.data?.sources ?? []
  const newCount = useMemo(
    () => entries.filter((entry) => (entry.occurredAt ?? '') >= openedAt.current).length,
    [entries],
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2 rounded-lg border border-amber-300/10 bg-[#0b0b0b] px-2.5 py-2">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/80">
          <Circle
            size={8}
            className={clsx(
              live ? 'fill-emerald-400 text-emerald-400' : 'fill-stone-600 text-stone-600',
              live && 'animate-pulse motion-reduce:animate-none',
            )}
          />
          Write ledger
        </span>

        <button
          type="button"
          onClick={() => setLive((prev) => !prev)}
          aria-pressed={live}
          className="flex items-center gap-1 rounded border border-amber-300/15 px-2 py-1 text-[10px] text-stone-300 transition hover:border-amber-300/30 hover:text-amber-100"
        >
          {live ? <Pause size={10} /> : <Play size={10} />}
          {live ? 'Pause' : 'Resume'}
        </button>

        <button
          type="button"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="flex items-center gap-1 rounded border border-amber-300/15 px-2 py-1 text-[10px] text-stone-300 transition hover:border-amber-300/30 hover:text-amber-100 disabled:opacity-40"
        >
          <RefreshCw size={10} className={clsx(query.isFetching && 'animate-spin')} /> Refresh
        </button>

        <label className="flex items-center gap-1.5 text-[10px] text-stone-500">
          <Table2 size={11} />
          <select
            value={tableFilter}
            onChange={(event) => setTableFilter(event.target.value)}
            aria-label="Filter ledger by table"
            className="rounded border border-amber-300/10 bg-black/40 px-1.5 py-1 text-[10px] text-stone-200 focus:border-amber-300/30 focus:outline-none"
          >
            <option value="">All tracked tables</option>
            {sources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </label>

        <span className="ml-auto text-[10px] text-stone-500">
          {count(entries.length)} rows loaded
          {newCount > 0 && (
            <span className="ml-1.5 rounded bg-emerald-500/15 px-1.5 py-0.5 text-emerald-300">
              {count(newCount)} since you opened this
            </span>
          )}
          {query.data && (
            <span className="ml-1.5 text-stone-600">
              polled {stamp(query.data.queriedAt)}
            </span>
          )}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-amber-300/10 bg-[#0c0c0c]">
        {query.isError ? (
          <p className="p-4 text-[11px] text-red-300">
            The ledger could not be read. The database may be unreachable — check the Health tab.
          </p>
        ) : entries.length === 0 ? (
          <p className="p-4 text-[11px] text-stone-500">
            {query.isLoading
              ? 'Reading the database…'
              : 'No rows found in the tracked tables yet. Perform an action in the platform and it will appear here.'}
          </p>
        ) : (
          <table className="w-full border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#121212]">
              <tr className="text-[9px] uppercase tracking-[0.12em] text-stone-500">
                <th scope="col" className="px-2.5 py-2 font-medium">
                  Written at
                </th>
                <th scope="col" className="px-2.5 py-2 font-medium">
                  Table
                </th>
                <th scope="col" className="px-2.5 py-2 font-medium">
                  Record
                </th>
                <th scope="col" className="px-2.5 py-2 font-medium">
                  Stored values
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const isNew = (entry.occurredAt ?? '') >= openedAt.current
                const open = expanded === entry.key
                const archived = entryArchived(entry)
                const dealId = entryDealId(entry, query.data?.viewerEnvironment)
                return (
                  <tr
                    key={entry.key}
                    // The second click of a double-click is not a second toggle;
                    // double-click opens the deal file (or every column).
                    onClick={(event) => {
                      if (event.detail > 1) return
                      setExpanded(open ? null : entry.key)
                    }}
                    onDoubleClick={() => {
                      if (dealId) openDealFile(dealId)
                      else setExpanded(entry.key)
                    }}
                    title={dealId ? 'Double-click to open the deal file' : undefined}
                    data-archived={archived ? 'true' : undefined}
                    className={clsx(
                      'cursor-pointer border-b border-white/[0.04] align-top text-[11px] transition',
                      isNew ? 'bg-emerald-500/[0.06]' : 'hover:bg-white/[0.02]',
                      archived && 'opacity-60',
                    )}
                  >
                    <td className="whitespace-nowrap px-2.5 py-1.5 font-mono text-[10px] text-stone-300">
                      {isNew && (
                        <span className="mr-1.5 rounded bg-emerald-500/20 px-1 py-0.5 text-[8px] uppercase tracking-wider text-emerald-300">
                          New
                        </span>
                      )}
                      {stamp(entry.occurredAt)}
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-1.5">
                      <div className="text-stone-200">{entry.label}</div>
                      <div className="font-mono text-[9px] text-stone-600">{entry.table}</div>
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-1.5 font-mono text-[10px] text-stone-400">
                      {entry.recordId ?? '—'}
                      {archived && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-stone-500/15 px-1 py-px font-sans text-[8px] uppercase tracking-wider text-stone-300 ring-1 ring-inset ring-stone-400/25">
                          <Archive size={7} /> Archived
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5">
                      {open ? (
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 xl:grid-cols-3">
                          {Object.entries(entry.row).map(([column, value]) => (
                            <div key={column} className="min-w-0">
                              <dt className="text-[9px] uppercase tracking-[0.1em] text-stone-600">
                                {column}
                              </dt>
                              <dd
                                className={clsx(
                                  'truncate',
                                  value === '[redacted]' ? 'text-amber-300/70' : 'text-stone-200',
                                )}
                                title={cell(value)}
                              >
                                {cell(value)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-stone-300">
                          {Object.entries(entry.summary).map(([column, value]) => (
                            <span key={column} className="truncate">
                              <span className="text-stone-600">{column}=</span>
                              {cell(value)}
                            </span>
                          ))}
                          <span className="text-[9px] text-stone-600">
                            {dealId ? 'click for every column · double-click opens the deal file' : 'click for every column'}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
